import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import * as api from '../adminApi'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { applySubscriptionAction } from '../../core/rules/subscription'
import { errorMessage } from '../../core/errors'
import { useAdmin } from '../AdminContext'
import type { Account, Appointment, Course, SkinPlan } from '../../core/types/account'
import type { SubscriptionAction } from '../../core/types/requests'
import type { BookingBlockRequest, CustomerFile, NewAppointmentRequest } from '../../core/types/adminRequests'
import type { RequestOptions } from '../../core/types/backend'

const putAppt = (a: Account, appt: Appointment): Account => ({
  ...a,
  appointments: a.appointments.map((x) => (x.id === appt.id ? appt : x)),
})

/**
 * Eén klantdossier in het beheer. Elke actie wordt meteen getoond, direct
 * bij de backend bewaard, en teruggezet als dat mislukt. Klantdata zit dus
 * nooit in het concept van "Opslaan / Verwerpen".
 */
export function useCustomerFile(id: string | undefined) {
  // Abonnementsregels uit het concept van Instellingen, niet de live site.
  const policy = useAdmin().site.policy
  const load = useCallback(
    (opts: RequestOptions) => (id ? api.fetchCustomer(id, opts) : Promise.resolve(null)),
    [id],
  )
  const { data: file, setData: setFile, status: loadStatus, reload, refresh } = useLatestLoad(load)
  const status = loadStatus === 'ready' && !file ? 'missing' : loadStatus

  const latest = useRef<CustomerFile | null>(null)
  latest.current = file

  // Welke onderdelen nu bewaard worden (bijv. een afspraak-id). Een tweede klik op hetzelfde
  // onderdeel wordt genegeerd, zodat "Afronden" niet twee keer naar de server gaat.
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set())
  const busyRef = useRef(new Set<string>())
  const markBusy = useCallback((key: string, on: boolean) => {
    if (on) busyRef.current.add(key)
    else busyRef.current.delete(key)
    setBusy(new Set(busyRef.current))
  }, [])

  /**
   * Optimistisch aanpassen, dan bewaren. Bij een fout draait alleen déze wijziging terug:
   * is er intussen niets anders veranderd, dan de vorige stand; anders het dossier opnieuw
   * van de server, zodat een eerdere, wél gelukte actie niet mee verdwijnt.
   */
  const run = useCallback(
    async <T>(
      key: string | readonly string[],
      optimistic: ((a: Account) => Account) | null,
      request: () => Promise<T>,
      merge: (a: Account, result: T) => Account,
      success?: string,
    ): Promise<boolean> => {
      const keys = typeof key === 'string' ? [key] : key
      const before = latest.current
      if (!before || keys.some((k) => busyRef.current.has(k))) return false
      keys.forEach((k) => markBusy(k, true))
      const shown = optimistic ? { ...before, account: optimistic(before.account) } : before
      if (optimistic) {
        latest.current = shown
        setFile(shown)
      }
      try {
        const result = await request()
        // Intussen naar een andere klant gegaan: het antwoord hoort niet bij dit dossier.
        const owner = before.account.customer.id
        setFile((f) => (f && f.account.customer.id === owner ? { ...f, account: merge(f.account, result) } : f))
        if (success) toast.success(success)
        return true
      } catch (err) {
        if (latest.current === shown) setFile(before)
        else refresh()
        toast.error('Dat lukte niet', { description: errorMessage(err) })
        return false
      } finally {
        keys.forEach((k) => markBusy(k, false))
      }
    },
    [setFile, refresh, markBusy],
  )

  const cid = id ?? ''

  const actions = {
    savePlan: (plan: SkinPlan) =>
      run('plan', (a) => ({ ...a, plan }), () => api.savePlan(cid, plan), (a, saved) => ({ ...a, plan: saved }), 'Huidplan bewaard'),

    saveCourse: (course: Course | null) =>
      run('course', (a) => ({ ...a, course }), () => api.saveCourse(cid, course), (a, saved) => ({ ...a, course: saved })),

    /**
     * Na "Kuur stoppen en terugbetalen" (KuurStopForm, nooit optimistisch): de kuur is weg, en de
     * server haalde sessienummers van open afspraken weg. Het dossier daarom ook even opnieuw ophalen.
     */
    kuurStopped: () => {
      setFile((f) => (f ? { ...f, account: { ...f.account, course: null } } : f))
      refresh()
    },

    // De notitie staat naast het account, niet erin. Bij een fout gaat alleen de notitie terug:
    // vroeger zette dit het hele dossier terug, ook een afspraak die intussen wél bevestigd was.
    saveNote: async (internalNote: string) => {
      const before = latest.current
      if (!before || busyRef.current.has('note')) return false
      markBusy('note', true)
      setFile((f) => (f ? { ...f, internalNote } : f))
      try {
        await api.saveNote(cid, internalNote)
        toast.success('Notitie bewaard')
        return true
      } catch (err) {
        setFile((f) =>
          f && f.account.customer.id === before.account.customer.id && f.internalNote === internalNote
            ? { ...f, internalNote: before.internalNote }
            : f,
        )
        toast.error('Dat lukte niet', { description: errorMessage(err) })
        return false
      } finally {
        markBusy('note', false)
      }
    },

    /** Niet optimistisch: de server zet datum en wie; bij een fout blijft alles zoals het was. */
    saveBookingBlock: async (req: BookingBlockRequest) => {
      const before = latest.current
      if (!before || busyRef.current.has('booking-block')) return false
      markBusy('booking-block', true)
      try {
        const { bookingBlock } = await api.saveBookingBlock(cid, req)
        setFile((f) => (f && f.account.customer.id === before.account.customer.id ? { ...f, bookingBlock } : f))
        toast.success(bookingBlock ? 'Online boeken staat uit voor deze klant' : 'Ze kan weer online boeken')
        return true
      } catch (err) {
        toast.error('Dat lukte niet', { description: errorMessage(err) })
        return false
      } finally {
        markBusy('booking-block', false)
      }
    },

    addAppointment: (req: NewAppointmentRequest) =>
      run('new-appointment', null, () => api.addAppointment(cid, req), (a, appt) => ({ ...a, appointments: [...a.appointments, appt] }), 'Afspraak ingepland'),

    confirmAppointment: (aid: string) =>
      run(
        aid,
        (a) => ({ ...a, appointments: a.appointments.map((x) => (x.id === aid ? { ...x, status: 'bevestigd' as const } : x)) }),
        () => api.confirmAppointment(cid, aid),
        putAppt,
        'Bevestigd — de klant krijgt een bericht',
      ),

    proposeAppointment: (aid: string, date: string, time: string) =>
      run(
        aid,
        (a) => ({ ...a, appointments: a.appointments.map((x) => (x.id === aid ? { ...x, date, time, status: 'bevestigd' as const } : x)) }),
        () => api.proposeAppointment(cid, aid, { date, time }),
        putAppt,
        'Nieuw tijdstip doorgegeven aan de klant',
      ),

    // Afronden gebeurt alleen in de agenda (met gebruikte producten en advies); het dossier linkt daarheen.

    removeAppointment: (aid: string) =>
      run(
        aid,
        (a) => ({ ...a, appointments: a.appointments.filter((x) => x.id !== aid) }),
        () => api.removeAppointment(cid, aid),
        (a) => a,
        'Afspraak geannuleerd',
      ),

    subscription: (action: SubscriptionAction, success?: string) =>
      run(
        'subscription',
        (a) => ({ ...a, subscription: applySubscriptionAction(a.subscription, action, policy.subscription) }),
        () => api.customerSubscriptionAction(cid, action),
        (a, subscription) => ({ ...a, subscription }),
        success,
      ),

    markDiary: (entryId: string, marketing: boolean) =>
      run(
        `diary-${entryId}`,
        (a) => ({ ...a, diary: a.diary.map((d) => (d.id === entryId ? { ...d, marketing } : d)) }),
        () => api.markDiaryEntry(cid, entryId, { marketing }),
        (a, entry) => ({ ...a, diary: a.diary.map((d) => (d.id === entry.id ? entry : d)) }),
      ),
  }

  /** Wordt dit onderdeel (bijv. een afspraak-id) nu bewaard? Knoppen staan dan uit. */
  const isBusy = (key: string) => busy.has(key)

  return { file, status, reload, isBusy, ...actions }
}

/** Het dossier nadat het geladen is: `file` is dan zeker aanwezig. */
export type CustomerFileControl = ReturnType<typeof useCustomerFile> & { file: CustomerFile }
